import { Buffer } from 'buffer';
import nacl from "tweetnacl";
import { createSessionWithSignature, genAuthNonce } from "./auth-api";
const NO_AMBIENT_SERVER_SESSION = "Server sessions are not process-global. Use createWalletClient({ keypair }) and call that wallet client's methods.";
/* A create that a concurrent clearSession()/setSession() invalidated must fail
 * closed rather than hand its stale, generation-mismatched session back to the
 * caller; the caller re-requests and gets a session for the current generation. */
const STALE_SESSION_CREATE = "Server session create was superseded by a concurrent clearSession()/setSession(); retry to obtain a fresh session.";
/* ------------------------------------------------------------------ */
/* SESSION MANAGER                                                     */
/* ------------------------------------------------------------------ */
export class ServerSessionManager {
    constructor(keypair, sessionFactory, sessionConfig) {
        /* Private cache (lives for the life of the instance) */
        this.session = null;
        /* Coalesce concurrent getSession() calls into a single in-flight request */
        this.pendingSession = null;
        /* Monotonic generation, bumped on clear/set. An in-flight createSession() only
         * commits its result to the cache if the generation still matches the value it
         * captured when it started - so a clearSession()/setSession() that happens WHILE
         * a create is in flight deterministically wins, and the late result can't
         * silently re-install a session the caller just cleared/replaced (#259). */
        this.generation = 0;
        this.keypair = keypair !== null && keypair !== void 0 ? keypair : null;
        this.sessionFactory = sessionFactory !== null && sessionFactory !== void 0 ? sessionFactory : null;
        this.sessionConfig = sessionConfig !== null && sessionConfig !== void 0 ? sessionConfig : null;
    }
    /**
     * Create a session manager for a specific keypair.
     * Each instance has its own independent session cache.
     *
     * `sessionConfig` (optional) pins the app+issuer the keypair signs/mints
     * against, so re-signing after expiry never follows a later global init().
     */
    static forKeypair(keypair, sessionConfig) {
        return new ServerSessionManager(keypair, undefined, sessionConfig);
    }
    /**
     * Create an isolated server session manager for a non-Solana signer (currently
     * EVM/SIWE). The factory must perform the signature exchange and return the
     * canonical signer address with its tokens; no signer state becomes global.
     */
    static forSessionFactory(factory) {
        if (typeof factory !== 'function')
            throw new Error('A server session factory is required');
        return new ServerSessionManager(undefined, factory);
    }
    /* ---------------------------------------------- *
     * Session creation (instance method)
     * ---------------------------------------------- */
    async createSession() {
        var _a;
        if (this.sessionFactory) {
            const session = await this.sessionFactory();
            if (!session || typeof session.address !== 'string' || !session.address ||
                typeof session.idToken !== 'string' || !session.idToken ||
                typeof session.accessToken !== 'string' || !session.accessToken ||
                typeof session.refreshToken !== 'string' || !session.refreshToken) {
                throw new Error('Server signer session factory returned an invalid session');
            }
            return session;
        }
        if (!this.keypair) {
            throw new Error(NO_AMBIENT_SERVER_SESSION);
        }
        const kp = this.keypair;
        const address = kp.publicKey.toBase58();
        const sessionConfig = (_a = this.sessionConfig) !== null && _a !== void 0 ? _a : undefined;
        /* fetch nonce from auth API (bound to the snapshotted app+issuer) */
        const nonce = await genAuthNonce(sessionConfig);
        const { genSolanaMessage } = await import("./sol/solana-message");
        const message = await genSolanaMessage(address, nonce, sessionConfig === null || sessionConfig === void 0 ? void 0 : sessionConfig.appId);
        /* sign the message */
        const sigBytes = nacl.sign.detached(new TextEncoder().encode(message), kp.secretKey);
        const signature = Buffer.from(sigBytes).toString("base64");
        /* call auth API (bound to the snapshotted app+issuer) */
        const { accessToken, idToken, refreshToken, } = await createSessionWithSignature(address, message, signature, undefined, sessionConfig);
        return { address, accessToken, idToken, refreshToken };
    }
    /* ---------------------------------------------- *
     * GET (lazy-fetch)
     * ---------------------------------------------- */
    async getSession() {
        if (this.session !== null) {
            return this.session;
        }
        // If a session creation is already in-flight, reuse that promise
        // instead of firing a second concurrent request.
        if (this.pendingSession !== null) {
            return this.pendingSession;
        }
        const startedGeneration = this.generation;
        // Capture the composed promise so BOTH the cache commit and the in-flight
        // pointer reset can verify this create is still the active one. A
        // clearSession()/setSession() during the create may have started a NEWER
        // create that now owns `pendingSession`; neither the commit nor the reset may
        // touch state that no longer belongs to this create.
        const pending = this.createSession()
            .then((session) => {
            if (this.generation !== startedGeneration) {
                // A clear/set won the race while this create was in flight. Do NOT
                // cache the now-invalidated session and do NOT return it to the
                // caller - fail closed so the caller re-requests against the current
                // generation instead of using a stale token.
                throw new Error(STALE_SESSION_CREATE);
            }
            this.session = session;
            return session;
        })
            .finally(() => {
            // Release the in-flight pointer ONLY if it still refers to THIS create.
            // A concurrent clearSession() nulled it and a newer create may already
            // own it; clearing unconditionally would erase that create's coalescing
            // and force later callers to redo the nonce/signature/session mint.
            if (this.pendingSession === pending) {
                this.pendingSession = null;
            }
        });
        this.pendingSession = pending;
        return pending;
    }
    /* ---------------------------------------------- *
     * STORE  (overwrites the cached value)
     * ---------------------------------------------- */
    setSession(session) {
        this.generation++;
        this.session = session;
    }
    /* ---------------------------------------------- *
     * CLEAR  (e.g. after explicit logout)
     * ---------------------------------------------- */
    clearSession() {
        this.generation++;
        this.session = null;
        this.pendingSession = null;
    }
    /* ---------------------------------------------- *
     * QUICK helpers
     * ---------------------------------------------- */
    isAuthenticated() {
        return this.session !== null;
    }
    getIdToken() {
        var _a, _b;
        return (_b = (_a = this.session) === null || _a === void 0 ? void 0 : _a.idToken) !== null && _b !== void 0 ? _b : null;
    }
    getRefreshToken() {
        var _a, _b;
        return (_b = (_a = this.session) === null || _a === void 0 ? void 0 : _a.refreshToken) !== null && _b !== void 0 ? _b : null;
    }
}
/* The default singleton exists only so top-level server calls fail closed. */
ServerSessionManager.instance = new ServerSessionManager();
